import { RSocketClient, JsonSerializer, IdentitySerializer, ClientConfig as RSocketClientConfig } from 'rsocket-core';
import { ReactiveSocket } from 'rsocket-types';
import RSocketWebSocketClient from 'rsocket-websocket-client';

import { EventSortOrder, Subscription, StreamMessage } from '../sportsbook-api-types';
import { StreamMessageTypeDto } from './sbapi-api-model';
import { focusManager, onlineManager } from '@tanstack/react-query';

const RECONNECT_INTERVAL_FACTOR_MS = 2000;
const MAX_RESUBSCRIBE_ATTEMPTS = 3;
const RESUBSCRIBE_INTERVAL_FACTOR_MS = 5000;
const MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT = 100000;

const EVENTS_ROUTE_V3 = '/v3/events';

const CF_MITIGATION_SESSION_KEY = 'SBAPI_RELOADED_DUE_TO_CF_MITIGATION';

type RSocketData = {
	filters: unknown;
	pageSize?: number;
	page?: number;
	order?: EventSortOrder;
};

type RSocketMetadata = string | Buffer | Uint8Array;

// Could not find model for this error so stubbing it
type SubscriptionError = {
	source?: { explanation: string } | undefined;
};
const isStreamTerminatedAfterReceivingAnError = (error: Error | SubscriptionError): boolean =>
	'source' in error &&
	error.source !== undefined &&
	['APPLICATION_ERROR', 'REJECTED', 'CANCELED', 'INVALID'].includes(error.source.explanation);

const createRSocketClient = (
	url: string,
	wsCreator?: ((url: string) => WebSocket) | undefined
): RSocketClient<RSocketData, RSocketMetadata> => {
	const config: RSocketClientConfig<RSocketData, RSocketMetadata> = {
		setup: {
			dataMimeType: 'application/json',
			keepAlive: 60 * 1000,
			lifetime: 2 * 60 * 1000,
			metadataMimeType: 'message/x.rsocket.routing.v0'
		},
		transport: new RSocketWebSocketClient({
			url,
			debug: true,
			wsCreator
		}),
		serializers: {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			data: JsonSerializer,
			metadata: IdentitySerializer
		}
	};

	return new RSocketClient(config);
};

const establishRSocketConnection = (url: string, wsCreator?: ((url: string) => WebSocket) | undefined) =>
	new Promise<ReactiveSocket<RSocketData, RSocketMetadata>>((resolve, reject) => {
		const client = createRSocketClient(url, wsCreator);
		client.connect().subscribe({ onComplete: resolve, onError: reject });
	});

const getNextResubscribeCount = (currentRetryCount: number, hasReceivedInitialState: boolean): number => {
	if (hasReceivedInitialState) {
		// Start a new retry process
		// Start on 1 since 0 is initial call by consumer that we don't count as a retry
		return 1;
	}

	return currentRetryCount + 1;
};

const getMsUntilNextResubscribe = (nextRetryCount: number, hasReceivedInitialState: boolean): number => {
	if (hasReceivedInitialState) {
		// Retry right away if we previously "established" a subscription i.e. received an initial state
		return 0;
	}

	// Back off some so we don't spam sbapi
	return nextRetryCount * RESUBSCRIBE_INTERVAL_FACTOR_MS;
};

function toHttpUrl(websocketUrl: string): string {
	return websocketUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

/**
 * Holds a websocket connection to SbApi
 * Keeps track of open subscriptions and ensures retries and refetching when reconnecting
 */
export default class SBApiWebsocketClient {
	private baseUrl: string;

	private options: SBApiWebsocketClientOptions;

	private connection: ReactiveSocket<RSocketData, RSocketMetadata> | null = null;

	// Array of all open subscriptions, will be used when subscribing and connecting
	private subscriptions: { [ref: string]: Subscription } = {};

	// If consumer has called connect (but not disconnect), so we know if we should try and keep a connection open
	private shouldBeConnected = false;

	// When establishing the actual connection, so we don't accidentally opens two connections
	private isConnecting = false;

	// Id of the retry timeout, so we can cancel an upcoming retry if we get connected (or consumer calls disconnect)
	private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

	// Usubscribe functions for focus and online manager
	private disposeFocusManagerSubscription?: () => void;

	private disposeOnlineManagerSubscription?: () => void;

	private setupVisibilityChangeHandler = () => {
		return focusManager.subscribe(() => {
			const isFocused = focusManager.isFocused();
			// Reconnect immediately if reconnect attempt is planned.
			if (isFocused && this.reconnectTimeoutId) {
				void this.reconnectAndKeepSubscriptions();
			}
		});
	};

	private setupOnlineChangeHandler = () => {
		return onlineManager.subscribe(() => {
			const isOnline = onlineManager.isOnline();
			// Going offline treated as a connection error. Suspending connection and notifying consumer.
			if (!isOnline) {
				this.suspendConnection();

				if (this.options.onConnectionError) {
					this.options.onConnectionError(0);
				}
			}

			// Going online, try to reconnect immediately.
			if (isOnline && !this.connection) {
				void this.reconnectAndKeepSubscriptions();
			}
		});
	};

	constructor(baseUrl: string, options?: SBApiWebsocketClientOptions | undefined) {
		this.baseUrl = baseUrl;
		this.options = options || {};
	}

	public getBaseUrl = () => this.baseUrl;

	public getOptions = () => this.options;

	public getSubscriptions = () => this.subscriptions;

	/**
	 * Connect to websocket, must be called before subscribing
	 */
	public connect = async () => {
		this.shouldBeConnected = true;

		// Subscribe to focus and online manager
		this.disposeFocusManagerSubscription = this.setupVisibilityChangeHandler();
		this.disposeOnlineManagerSubscription = this.setupOnlineChangeHandler();

		await this.connectWithRetry();
	};

	/**
	 * Disconnect from websocket and unsubscribes to all current subscriptions
	 */
	public disconnect = () => {
		if (this.connection) {
			try {
				Object.keys(this.subscriptions).forEach(ref => this.unsubscribeFromEvents(ref));
				this.connection.close();
			} catch {
				// Do nothing
			}

			if (this.reconnectTimeoutId) {
				clearTimeout(this.reconnectTimeoutId);
			}
			if (this.disposeFocusManagerSubscription) {
				this.disposeFocusManagerSubscription();
			}
			if (this.disposeOnlineManagerSubscription) {
				this.disposeOnlineManagerSubscription();
			}

			this.shouldBeConnected = false;
			this.connection = null;
			this.reconnectTimeoutId = null;
			this.subscriptions = {};
			this.disposeFocusManagerSubscription = undefined;
			this.disposeOnlineManagerSubscription = undefined;
		}
	};

	/**
	 * Disconnect, but keep subscriptions and all other state.
	 * Call this when you want to temporarily disconnect (for example, because online status changed), but want to reconnect later and keep all subscriptions.
	 */
	private suspendConnection = () => {
		if (this.connection) {
			try {
				this.connection.close();
			} catch {
				// Do nothing
			}

			if (this.reconnectTimeoutId) {
				clearTimeout(this.reconnectTimeoutId);
			}

			this.connection = null;
		}
	};

	/**
	 * Try to disconnect and then reconnect to websocket. Keep all subscriptions
	 */
	public reconnectAndKeepSubscriptions = () => {
		if (this.isConnecting) {
			return;
		}

		// Terminate any planned reconnect attempts.
		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		const preservedSubscriptions = { ...this.subscriptions };
		this.disconnect();
		this.subscriptions = preservedSubscriptions;

		return this.connect();
	};

	/**
	 * Start subscribing to provided subscription
	 * @param subscription Subscription to be subscribed to
	 */
	public subscribeToEvents = (subscription: Subscription) => {
		if (this.hasSubscription(subscription.ref)) {
			return;
		}

		this.subscriptions[subscription.ref] = subscription;

		if (!this.connection) {
			// Consumer has not called connect() yet or we are currently trying to re/connect
			// The re/connect logic will re/subscribe once a connection has been established
			// So we don't need to do anything more here
			return;
		}

		this.subscribeToEventsWithRetry({ ref: subscription.ref });
	};

	/**
	 * Completely unsubscribes from a reference
	 * @param ref Subscription reference
	 */
	public unsubscribeFromEvents = (ref: string) => {
		if (!this.hasSubscription(ref)) {
			return;
		}

		const subscription = this.subscriptions[ref];
		if (subscription.cancel) {
			try {
				subscription.cancel();
			} catch {
				// Do nothing if we can't cancel
			}
		}

		delete this.subscriptions[ref];
	};

	/**
	 * Check if a subscription is active
	 * @param ref Subscription reference
	 */
	public hasSubscription = (ref: string) => {
		return !!this.subscriptions[ref];
	};

	private shouldReloadDueToCloudflareChallenge = async (): Promise<boolean> => {
		try {
			const res = await fetch(toHttpUrl(this.baseUrl), { method: 'HEAD' });

			const cfMitigated = res.headers.get('cf-mitigated');
			return cfMitigated === 'challenge';
		} catch (error) {
			return false;
		}
	};

	private maybeReloadForCloudflare = async () => {
		if (sessionStorage.getItem(CF_MITIGATION_SESSION_KEY)) {
			return;
		}

		if (await this.shouldReloadDueToCloudflareChallenge()) {
			sessionStorage.setItem(CF_MITIGATION_SESSION_KEY, 'true');
			window.location.reload();
		}
	};

	private connectWithRetry = async (retryCount = 0) => {
		if (!this.shouldBeConnected || this.isConnecting || this.connection) {
			return;
		}

		// Reload a page if we are behind a Cloudflare challenge
		void this.maybeReloadForCloudflare();

		this.isConnecting = true;
		try {
			this.connection = await establishRSocketConnection(this.baseUrl, this.options.wsCreator);
		} catch (e) {
			if (this.options.onConnectionError) {
				this.options.onConnectionError(retryCount);
			}
		}
		this.isConnecting = false;

		if (!this.shouldBeConnected) {
			// Consumer called disconnect while connecting, lets keep it disconnected
			this.disconnect();
		} else if (this.connection) {
			sessionStorage.removeItem(CF_MITIGATION_SESSION_KEY);

			// Connection established, notify and handle it
			if (this.options.onConnected) {
				this.options.onConnected();
			}
			this.handleNewConnection(this.connection, this.connectWithRetry);
		} else {
			// Establishing connection failed, but should be connected, try reconnect.
			this.reconnectTimeoutId = setTimeout(
				// eslint-disable-next-line @typescript-eslint/no-misused-promises
				() => this.connectWithRetry(retryCount + 1),
				RECONNECT_INTERVAL_FACTOR_MS * retryCount
			);
		}
	};

	private handleNewConnection = (
		newConnection: ReactiveSocket<RSocketData, RSocketMetadata>,
		reconnect: () => Promise<void>
	) => {
		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		Object.keys(this.subscriptions).forEach(ref => {
			this.subscribeToEventsWithRetry({ ref });
		});

		newConnection.connectionStatus().subscribe(event => {
			if (event.kind === 'ERROR' || event.kind === 'CLOSED') {
				try {
					newConnection.close();
				} catch {
					// Do nothing
				}
				if (newConnection === this.connection) {
					this.connection = null;
					// Something happened to the connection, try to reconnect
					if (this.shouldBeConnected) {
						this.reconnectTimeoutId = setTimeout(
							// eslint-disable-next-line @typescript-eslint/no-misused-promises
							reconnect,
							RECONNECT_INTERVAL_FACTOR_MS
						);
					}
				}
			}
		});
	};

	private subscribeToEventsWithRetry = ({
		ref,
		retryCount = 0,
		errorCausedRetry
	}: {
		ref: string;
		retryCount?: number;
		errorCausedRetry?: Error;
	}) => {
		const currentSubscription = this.subscriptions[ref];
		if (retryCount > MAX_RESUBSCRIBE_ATTEMPTS && currentSubscription?.onMaxResubscribeAttemptsReached) {
			currentSubscription.onMaxResubscribeAttemptsReached(retryCount, errorCausedRetry);
		}

		if (!this.connection || !currentSubscription) {
			return;
		}

		const { filters, orderBy, page, pageSize, onSubscribed, onMessage, onError } = currentSubscription;

		const requestData = {
			filters,
			pageSize,
			page,
			order: orderBy
		};

		const route = EVENTS_ROUTE_V3;

		let hasReceivedInitialState = false;

		this.connection
			.requestStream({
				data: requestData,
				metadata: String.fromCharCode(route.length) + route
			})
			.subscribe({
				onSubscribe: ({ cancel, request }) => {
					this.subscriptions[ref] = {
						...this.subscriptions[ref],
						pendingRequestCount: MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT,
						cancel,
						request
					};

					request(MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT);

					onSubscribed();
				},
				onError: e => {
					if (isStreamTerminatedAfterReceivingAnError(e)) {
						// Then we want to resubscribe.

						const nextRetryCount = getNextResubscribeCount(retryCount, hasReceivedInitialState);
						const msUntilNextRetry = getMsUntilNextResubscribe(nextRetryCount, hasReceivedInitialState);

						setTimeout(
							() =>
								this.subscribeToEventsWithRetry({
									ref,
									retryCount: nextRetryCount,
									errorCausedRetry: e
								}),
							msUntilNextRetry
						);
					} else {
						onError('Error in rsocket stream', e);
					}
				},
				onNext: msg => {
					// TODO Fix conversion
					const data = <StreamMessage[]>(<unknown>msg.data);

					const initialMessage = data.find(d => d.type === StreamMessageTypeDto.INITIAL_STATE);

					if (initialMessage) {
						hasReceivedInitialState = true;
					}

					onMessage(data);

					const subscription = this.subscriptions[ref];
					if (!subscription.pendingRequestCount || !subscription.request) {
						throw new Error('RSocket subscription missing properties');
					}

					// Make sure we let backend know we want more
					subscription.pendingRequestCount -= 1;
					if (subscription.pendingRequestCount === 0) {
						subscription.pendingRequestCount = MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT;
						subscription.request(MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT);
					}
				}
			});
	};
}

// Singleton
let sbApiWebsocketClient: SBApiWebsocketClient | null = null;

export type SBApiWebsocketClientOptions = {
	wsCreator?: ((url: string) => WebSocket) | undefined;
	onConnected?: () => void;
	onConnectionError?: (reconnectAttempt: number) => void;
};

export const createSBApiWebsocketClient = (websocketUrl: string, options?: SBApiWebsocketClientOptions | undefined) => {
	sbApiWebsocketClient = new SBApiWebsocketClient(websocketUrl, options);
};

export const getSBApiWebsocketClient = () => {
	if (sbApiWebsocketClient == null) {
		throw new Error('Sportsbook websocket client has not been configure');
	}
	return sbApiWebsocketClient;
};

// HMR for Singleton
// add the old client when disposing the module
// and then create a new one with the same settings and subscriptions
if (import.meta && import.meta.webpackHot) {
	import.meta.webpackHot.dispose(data => {
		// eslint-disable-next-line
		data.sbApiWebsocketClient = sbApiWebsocketClient;
	});

	// eslint-disable-next-line
	if (import.meta.webpackHot.data?.sbApiWebsocketClient) {
		const oldClient = <
			SBApiWebsocketClient // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		>import.meta.webpackHot.data.sbApiWebsocketClient;
		createSBApiWebsocketClient(oldClient.getBaseUrl(), oldClient.getOptions());
		const oldSubscriptions = oldClient.getSubscriptions();

		Object.keys(oldSubscriptions).forEach(ref => {
			getSBApiWebsocketClient().subscribeToEvents({
				ref: oldSubscriptions[ref].ref,
				filters: oldSubscriptions[ref].filters,
				onSubscribed: oldSubscriptions[ref].onSubscribed,
				onMessage: oldSubscriptions[ref].onMessage,
				onError: oldSubscriptions[ref].onError,
				orderBy: oldSubscriptions[ref].orderBy,
				page: oldSubscriptions[ref].page,
				pageSize: oldSubscriptions[ref].pageSize
			});
		});
		oldClient.disconnect();
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		getSBApiWebsocketClient().connect();
	}
}
